import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  InputAdornment,
  TextField,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import SiteListTable from 'src/components/sites/SiteListTable';
import NewSite from 'src/components/sites/NewSite';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';

const SiteList = ({ location }) => {
  const { search, setSearch } = usePersistentListFilters();
  const theme = useTheme();
  const { getUser } = useAuth();
  const isAdmin = isAdminUser(getUser);
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(search);

  const handleSearch = (e) => { setSearchText(e.target.value); setSearch(e.target.value); };

  return (
    <>
      <Helmet>
        <title>Sites | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF', // Clean White Canvas
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* --- Unified Header Section --- */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{
              py: 2,
              mb: 2,
              flexShrink: 0
            }}
          >
            {/* Left: Title & Subtitle */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                SITES
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage fuel stations, assets, and operational status.
              </Typography>
            </Box>

            {/* Right: Actions (Search + Button merged) */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                placeholder="Search sites..."
                size="small"
                value={searchText}
                onChange={handleSearch}
                sx={{
                  width: { xs: '100%', md: 320 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: theme.palette.primary.main, // Use Brand Primary Color
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  Add Site
                </Button>
              )}
            </Stack>
          </Stack>

          {/* --- Content Section --- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <SiteListTable
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
            />
          </Box>

          <NewSite
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />
        </Container>
      </Box>
    </>
  );
};

SiteList.propTypes = {
  location: PropTypes.object
};

export default SiteList;
